import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { MatLegacySelect as MatSelect } from "@angular/material/legacy-select";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { hasPermissionByKey, hasUserRoleManager, isAdmin, isSubordinate, isSupervisor } from "@app/_helpers/permission";
import { isComegoLockedForDate, isComegoLockedForDateFuture } from "@app/_helpers/timeLock";
import { coerceTimeFormat, createRxValue, fromRxValue, getActiveSchedule, stringify } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import {
	ComeAndGoCreateDialogComponent,
	WorkingHoursCreateData,
} from "@app/components/come-and-go-create-dialog/come-and-go-create-dialog.component";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { CUSTOM_TIME_RANGE_PRESET, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { ComegoService, ComegoTime, UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { addDays, addSeconds, endOfDay, isValid, startOfDay } from "date-fns/esm";
import { combineLatest, distinctUntilChanged, map, startWith, switchMap } from "rxjs";
import {
	cloneAbsenceIntervalsByUser,
	cloneConflictsMap,
	computeAbsenceConflicts,
} from "../absence-conflict-dialog/absence-conflict.utils";
import {
	AbsenceConflictCloseResult,
	AbsenceConflictDialogComponent,
} from "../absence-conflict-dialog/absence-conflict-dialog.component";
import { parseDurationSeconds } from "../duration-input-control/duration-input-utils";
import { EmployeeFilterPopoverComponent } from "../employee-filter-popover/employee-filter-popover.component";
import { ConfirmDialogComponent } from "../generic-dialogs/confirm-dialog/confirm-dialog.component";

const DEFAULT_ABSENCE_START = "08:00";

/** Minimal row shape returned by schedule generation (time strings HH:mm). */
type ScheduleTimeRow = {
	name?: string;
	start: string;
	end: string;
	type: string;
};

type AbsenceSubcategory = {
	id: string;
	enabled: boolean;
	title: string;
	visibleUsers: string[];
	visibleForGroups: string[];
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
};

type AbsenceSubcategorySelection = AbsenceSubcategory | string | null;

export type AbsenceEmployeeRef = {
	id: string;
	name: string;
};

export type AbsenceRowValue = {
	date: Date;
	target: string;
	type: AbsenceSubcategorySelection | "";
	hours: string;
	start: string;
	end: string;
};

export type AbsenceEmployeeValue = {
	employee: AbsenceEmployeeRef;
	absences: AbsenceRowValue[];
	targetDuration: number;
	summedDuration: number;
};

export type AbsenceDialogGroupValue = {
	initialType: string | null;
	employees: AbsenceEmployeeValue[];
};

type AbsenceRowControls = {
	date: FormControl<Date>;
	target: FormControl<string>;
	type: FormControl<AbsenceSubcategorySelection | "">;
	hours: FormControl<string>;
	start: FormControl<string>;
	end: FormControl<string>;
};

export type AbsenceRowControl = FormGroup<AbsenceRowControls>;

type AbsenceEmployeeControls = {
	employee: FormControl<AbsenceEmployeeRef>;
	absences: FormArray<AbsenceRowControl>;
	targetDuration: FormControl<number>;
	summedDuration: FormControl<number>;
};

export type AbsenceEmployeeControl = FormGroup<AbsenceEmployeeControls>;

type AbsenceDialogGroupControls = {
	initialType: FormControl<string | null>;
	employees: FormArray<AbsenceEmployeeControl>;
};

export type AbsenceDialogGroup = FormGroup<AbsenceDialogGroupControls>;

export function generateTimeTableFromSchedule(user: UserSettings, weekDate: Date = new Date()) {
	const schedule = getActiveSchedule(user, weekDate);
	if (!schedule?.enabled || !schedule.items?.length) return null;
	const day = schedule.items?.find((x) => x.enabled && x.day === startOfDay(weekDate).getUTCDay());
	if (!day) return null;
	const times =
		day.items?.reduce((acc, r, i, arr) => {
			const nextItem = arr[i + 1];
			const addPauseItem = nextItem && r.to !== nextItem.from;
			acc.push({
				name: "comego.work",
				start: r.from,
				end: r.to,
				type: "work",
			} as ScheduleTimeRow);
			if (addPauseItem)
				acc.push({
					name: "comego.pause",
					start: r.to,
					end: nextItem.from,
					type: "pause",
				} as ScheduleTimeRow);
			return acc;
		}, [] as ScheduleTimeRow[]) || [];
	return times;
}

export interface WorkingHoursAbsenceData {
	initialDate?: Date;
	returnToCreateDialog?: boolean;
}

@UntilDestroy()
@Component({
	selector: "tg-come-and-go-absence-dialog",
	templateUrl: "./come-and-go-absence-dialog.component.html",
	styleUrls: ["./come-and-go-absence-dialog.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComeAndGoAbsenceDialogComponent implements OnInit, OnDestroy {
	@ViewChild("employeeFilterPopover", { static: true }) employeeFilterPopover: EmployeeFilterPopoverComponent;
	@ViewChild("initialTypeSelect") initialTypeSelect?: MatSelect;
	readonly absenceWithSupervisors = createRxValue(false);
	readonly employeeFilter = createRxValue<string[]>([]);
	readonly employeeFilterPopoverOpen = createRxValue(true);
	readonly range = createRxValue<TRange>(CUSTOM_TIME_RANGE_PRESET);
	absenceSubcategories: AbsenceSubcategory[] = [];

	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x) || hasUserRoleManager(x))
	);
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	// Map structure: Map<userId: string, Set<dayKey: string>>
	readonly _dayConflicts$ = createRxValue<Map<string, Set<string>>>(new Map());
	readonly allWorkinghours$ = createRxValue<ComegoTime[]>([]);
	readonly canDeleteTimes = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes")))
	);
	readonly returnToCreateDialog$ = createRxValue(this.data.returnToCreateDialog);
	constructor(
		private ref: MatDialogRef<ComeAndGoAbsenceDialogComponent>,
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private app: AppService,
		private comegoService: ComegoService,
		private recordService: RecordToolbarService,
		private translate: TranslateService,
		private changeDetectorRef: ChangeDetectorRef,
		@Inject(MAT_DIALOG_DATA)
		private data: WorkingHoursAbsenceData
	) {
		if (!this.data) this.data = {};
	}

	readonly isLoading = createRxValue(false);
	readonly absenceGroup: AbsenceDialogGroup = new FormGroup({
		initialType: new FormControl<string | null>(null, Validators.required),
		employees: new FormArray<AbsenceEmployeeControl>([], [Validators.required, Validators.minLength(1)]),
	});

	/** Submit uses per-row absence types; initialType is required for UX only. */
	canSubmitAbsence(): boolean {
		const employees = this.absenceGroup.controls.employees;
		return employees.length > 0 && employees.valid;
	}

	private resolveInitialTypeSelection(): AbsenceSubcategorySelection {
		const raw = this.absenceGroup.controls.initialType.value;
		if (raw == null || raw === "") {
			return null;
		}
		if (raw === "absence") {
			return "absence";
		}
		return this.absenceSubcategories.find((s) => s.id === raw) ?? null;
	}

	private getAbsenceCloseStateSnapshot(): string {
		return stringify({
			range: {
				from: this.range.value?.from?.toISOString?.(),
				to: this.range.value?.to?.toISOString?.(),
				rangeType: this.range.value?.rangeType,
			},
			employeeFilter: this.employeeFilter.value,
			employees: this.absenceGroup.getRawValue()?.employees ?? [],
		});
	}

	private initialAbsenceCloseState: string | null = null;

	private hasAbsenceValuesChanged(): boolean {
		if (!this.range.value?.from) return false;
		if (!this.initialAbsenceCloseState) return false;
		return this.getAbsenceCloseStateSnapshot() !== this.initialAbsenceCloseState;
	}

	ngOnInit(): void {
		this.ref.updateSize("640px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);

		this.ref.disableClose = true;

		this.ref.backdropClick().subscribe(() => {
			this.handleClose();
		});

		const user = this.userSettingsQuery.getValue();
		this.absenceSubcategories =
			(user?.workspace as { workinghours?: { absenceSubcategories: typeof this.absenceSubcategories } })
				?.workinghours?.absenceSubcategories || [];
		this.absenceSubcategories = this.absenceSubcategories.filter((s) => s.enabled);

		const initialTypeControl = this.absenceGroup.controls.initialType;
		initialTypeControl.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
			this.applyAbsenceSubcategorySelection();
		});

		combineLatest([this.employeeFilter.asObservable(), this.range.asObservable()])
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.absenceWithSupervisors.update(false);
				this.updateAbsences();
			});

		const canManageOthers = this.workspace$canManageOthers.value;
		if (canManageOthers) {
			this.employeeFilter.next([]);
			this.employeeFilterPopover.selectedItems = [];
		} else {
			this.employeeFilter.next([user.id]);
			this.employeeFilterPopover.selectedItems = this.employeeFilter.value;
		}

		if (this.data.initialDate) {
			this.range.next({
				from: this.data.initialDate,
				to: this.data.initialDate,
				rangeType: "day",
			});
		}

		this.employeeFilterPopover.opened.pipe(untilDestroyed(this)).subscribe(() => {
			this.employeeFilterPopoverOpen.next(true);
		});
		this.employeeFilterPopover.closed.pipe(untilDestroyed(this)).subscribe(() => {
			this.employeeFilterPopoverOpen.next(false);
		});

		this.initialAbsenceCloseState = this.getAbsenceCloseStateSnapshot();

		this.ref.afterOpened().subscribe(() => {
			setTimeout(() => {
				if (!this.workspace$canManageOthers.value) {
					return;
				}
				if (this.employeeFilter.value.length > 0) {
					return;
				}
				this.employeeFilterPopover.open();
			}, 0);
		});

		const absenceDurationsForConflicts$ = this.absenceGroup.valueChanges.pipe(
			startWith(this.absenceGroup.getRawValue()),
			map(() => this.getAbsenceDurationSnapshot()),
			distinctUntilChanged(),
			debounceTimeAfterFirst(200)
		);

		combineLatest([this.range.asObservable(), this.employeeFilter.asObservable(), absenceDurationsForConflicts$])
			.pipe(
				untilDestroyed(this),
				switchMap(async ([range, employees]) => {
					if (!range?.from || !range?.to || !employees.length) {
						this.allWorkinghours$.update([]);
						return new Map<string, Set<string>>();
					}

					const workingTimes = await this.comegoService.getBetween(range.from, range.to);
					const employeeIds = new Set(employees);
					const filteredByEmployee = workingTimes.filter((wh) => employeeIds.has(wh.user.id));
					const absenceIntervalsByUser = this.buildAbsenceIntervalsByUser(employeeIds);
					const { conflicts, relevantWorkingHours } = computeAbsenceConflicts(
						filteredByEmployee,
						absenceIntervalsByUser
					);

					this.allWorkinghours$.update(relevantWorkingHours);
					return conflicts;
				})
			)
			.subscribe((map) => {
				this._dayConflicts$.next(map);
				this.changeDetectorRef.markForCheck();
			});
	}

	ngOnDestroy(): void {
		// no-op
	}

	setRange(r: TRange) {
		this.range.next(r);
		this.openInitialTypeSelectIfNeeded();
	}

	private openInitialTypeSelectIfNeeded(): void {
		setTimeout(() => {
			const initialType = this.absenceGroup.controls.initialType.value;
			if (initialType != null && initialType !== "") {
				return;
			}
			if (!this.range.value?.from || !this.range.value?.to) {
				return;
			}
			this.initialTypeSelect?.open();
			this.absenceGroup.controls.initialType.markAsTouched();
		}, 0);
	}
	getDatesOfRange(from: Date, to: Date): Date[] {
		const dates: Date[] = [];
		let currentDate = from;
		while (currentDate <= to) {
			dates.push(currentDate);
			currentDate = addDays(currentDate, 1);
		}
		return dates;
	}
	updateAbsences(): void {
		const employeesArray = this.absenceGroup.controls.employees;
		employeesArray.clear();
		const initialType = this.resolveInitialTypeSelection();
		const dates = this.getDatesOfRange(this.range.value.from, this.range.value.to);
		const userSettings = this.userSettingsQuery.getValue();
		const employees = userSettings.workspace.users.filter((user) => this.employeeFilter.value.includes(user.id));
		for (const user of employees) {
			const employeeGroup = new FormGroup<AbsenceEmployeeControls>({
				employee: new FormControl({ id: user.id, name: user.name }),
				absences: new FormArray<AbsenceRowControl>([]),
				targetDuration: new FormControl(0),
				summedDuration: new FormControl(0),
			});
			const absencesArray = employeeGroup.controls.absences;
			const allowedInitialType =
				initialType == null ||
				initialType === "absence" ||
				(typeof initialType === "object" && this.absencesForUser(initialType, employeeGroup))
					? initialType
					: null;
			for (const date of dates) {
				if (userSettings.id == user.id) {
					if (
						date &&
						(isComegoLockedForDate(userSettings, date) || isComegoLockedForDateFuture(userSettings, date))
					) {
						this.absenceWithSupervisors.update(true);
						return;
					}
				}
				const target = this.getScheduleForUser(user.id, date);
				const hours = allowedInitialType
					? this.getAbsenceSubcategoryDuration(allowedInitialType, target)
					: "00:00";

				const absenceRow = new FormGroup<AbsenceRowControls>({
					date: new FormControl(date, [Validators.required]),
					target: new FormControl(target),
					type: new FormControl(allowedInitialType ?? "", [Validators.required]),
					hours: new FormControl(hours, [
						Validators.required,
						Validators.pattern(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
					]),
					start: new FormControl(DEFAULT_ABSENCE_START, [
						Validators.required,
						Validators.pattern(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
					]),
					// Enable/disable is synced with hours via syncDurationEditability (reactive
					// forms ignore a template [disabled] binding).
					end: new FormControl({ value: hours, disabled: true }),
				});

				absenceRow.controls.type.valueChanges.pipe(untilDestroyed(this)).subscribe((selectedType) => {
					const customDuration = this.getAbsenceSubcategoryDuration(
						selectedType,
						absenceRow.controls.target.value
					);
					absenceRow.controls.hours.setValue(customDuration);
					this.syncDurationEditability(absenceRow, employeeGroup);
				});

				absenceRow.controls.hours.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
					this.recalculateEmployeeDurations(employeeGroup);
					this.updateAbsenceRowEnd(absenceRow);
				});

				absenceRow.controls.start.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
					if (this.isDisabledForUser(absenceRow, employeeGroup)) {
						this.updateAbsenceRowEnd(absenceRow);
					} else {
						this.updateAbsenceRowHoursFromEnd(absenceRow);
					}
				});

				absenceRow.controls.end.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
					this.updateAbsenceRowHoursFromEnd(absenceRow);
					this.recalculateEmployeeDurations(employeeGroup);
				});

				this.updateAbsenceRowEnd(absenceRow);
				this.syncDurationEditability(absenceRow, employeeGroup);

				absencesArray.push(absenceRow);
			}

			this.recalculateEmployeeDurations(employeeGroup);
			employeesArray.push(employeeGroup);
		}
	}
	private dayKey(d: Date): string {
		return formatDate(d, "yyyy-MM-dd", "en-US");
	}

	/** Derive end from start + duration (hours). */
	private updateAbsenceRowEnd(absenceRow: AbsenceRowControl): void {
		const date = absenceRow.controls.date.value;
		if (!date || !isValid(date)) return;

		const startDate = coerceTimeFormat(absenceRow.controls.start.value || DEFAULT_ABSENCE_START, date);
		if (!isValid(startDate)) return;

		const endTime = addSeconds(startDate, parseDurationSeconds(absenceRow.controls.hours.value));
		const v = formatDate(endTime, "HH:mm", "en-US");
		absenceRow.controls.end.setValue(v, { emitEvent: false });
		this.changeDetectorRef.markForCheck();
	}

	/** Derive duration (hours) from start → end when the user edits end. */
	private updateAbsenceRowHoursFromEnd(absenceRow: AbsenceRowControl): void {
		const start = absenceRow.controls.start.value || DEFAULT_ABSENCE_START;
		const end = absenceRow.controls.end.value;
		if (!end) return;

		const startDate = coerceTimeFormat(start, absenceRow.controls.date.value);
		const endDate = coerceTimeFormat(end, absenceRow.controls.date.value);

		const diffMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
		const safeMinutes = diffMinutes > 0 ? diffMinutes : 0;
		const hours = Math.floor(safeMinutes / 60);
		const minutes = safeMinutes % 60;
		absenceRow.controls.hours.setValue(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`, {
			emitEvent: false,
		});
		this.changeDetectorRef.markForCheck();
	}

	private recalculateEmployeeDurations(employeeGroup: AbsenceEmployeeControl): void {
		const absencesArray = employeeGroup.controls.absences;
		let targetDuration = 0;
		let summedDuration = 0;

		absencesArray.controls.forEach((absence) => {
			targetDuration += parseDurationSeconds(absence.controls.target.value);
			summedDuration += parseDurationSeconds(absence.controls.hours.value);
		});

		employeeGroup.controls.targetDuration.setValue(targetDuration, { emitEvent: false });
		employeeGroup.controls.summedDuration.setValue(summedDuration, { emitEvent: false });
		this.changeDetectorRef.markForCheck();
	}

	getEmployeeDurationDifference(employeeGroup: AbsenceEmployeeControl): number {
		return employeeGroup.controls.summedDuration.value - employeeGroup.controls.targetDuration.value;
	}
	getScheduleForUser(userId: string, date: Date) {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);

		if (!times || times.length == 0) return "00:00";

		const hours: string[] = [];
		times
			.filter((time) => {
				return time.type == "work";
			})
			.forEach((time) => {
				hours.push(this.calculateScheduleDuration(time));
			});

		return this.sumTimes(hours);
	}
	calculateScheduleDuration(entry: ScheduleTimeRow): string {
		const [startHour, startMinute] = entry.start.split(":").map(Number);
		const [endHour, endMinute] = entry.end.split(":").map(Number);

		const startTotal = startHour * 60 + startMinute;
		const endTotal = endHour * 60 + endMinute;

		const diffMinutes = endTotal - startTotal;

		const hours = Math.floor(diffMinutes / 60);
		const minutes = diffMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	}
	sumTimes(times: string[]): string {
		let totalMinutes = 0;

		times.forEach((time) => {
			const [hours, minutes] = time.split(":").map(Number);
			totalMinutes += hours * 60 + minutes;
		});

		const sumHours = Math.floor(totalMinutes / 60);
		const sumMinutes = totalMinutes % 60;

		return `${String(sumHours).padStart(2, "0")}:${String(sumMinutes).padStart(2, "0")}`;
	}
	applyAbsenceSubcategorySelection() {
		const selectedType = this.resolveInitialTypeSelection();
		const employeesArray = this.absenceGroup.controls.employees;
		employeesArray.controls.forEach((employeeGroup) => {
			const absencesArray = employeeGroup.controls.absences;
			absencesArray.controls.forEach((absenceRow) => {
				const target = absenceRow.controls.target.value;
				if (selectedType == null) {
					absenceRow.controls.type.setValue("");
					absenceRow.controls.hours.setValue(target);
					return;
				}
				const allowed =
					selectedType === "absence" ||
					(typeof selectedType === "object" && this.absencesForUser(selectedType, employeeGroup));
				if (!allowed) {
					return;
				}

				absenceRow.controls.type.setValue(selectedType);
				const customDuration = this.getAbsenceSubcategoryDuration(selectedType, target);
				absenceRow.controls.hours.setValue(customDuration);
			});
			this.recalculateEmployeeDurations(employeeGroup);
		});
	}
	disableAbsence(i: number, j: number) {
		const employeesArray = this.absenceGroup.controls.employees;
		const employee = employeesArray.controls[i];
		const absencesArray = employee.controls.absences;
		if (absencesArray.length === 1) {
			employeesArray.removeAt(i);
			return;
		}
		absencesArray.removeAt(j);
		this.recalculateEmployeeDurations(employee);
	}

	async checkBeforeClose(): Promise<boolean> {
		if (this.hasAbsenceValuesChanged()) {
			const discardChanges = await new Promise<boolean>((resolve) => {
				this.dialog
					.open(ConfirmDialogComponent, {
						data: {
							text: this.translate.instant("comego.dialog.close-unsaved-absence-text"),
							strings: {
								confirm: this.translate.instant("utils.close"),
								cancel: this.translate.instant("utils.cancel"),
							},
						},
						disableClose: true,
						closeOnNavigation: false,
						position: { top: "16px" },
						width: "520px",
					})
					.afterClosed()
					.subscribe(resolve);
			});
			return discardChanges;
		}
		return true;
	}

	async handleClose(): Promise<void> {
		const canClose = await this.checkBeforeClose();
		if (canClose) {
			this.ref.close();
		}
	}

	@HostListener("document:keydown.escape")
	onEscape(): void {
		this.handleClose();
	}

	getAbsenceSubcategoryDuration(selectedType: AbsenceSubcategorySelection, defaultDuration: string): string {
		if (!selectedType || typeof selectedType === "string") {
			return defaultDuration;
		}

		if (selectedType.durationType === "customValue" && selectedType.duration) {
			return selectedType.duration;
		}

		return defaultDuration;
	}

	isDisabledForUser(absence: AbsenceRowControl, empGroup: AbsenceEmployeeControl): boolean {
		const currentUser = this.userSettingsQuery.getValue();

		if (isAdmin(currentUser)) return false;
		const employeeId = empGroup.controls.employee.value.id;
		if (isSubordinate(currentUser, employeeId)) return false;
		const absenceType = absence.controls.type.value;
		if (typeof absenceType !== "object" || absenceType == null) return false;

		return !!absenceType.isStatic;
	}

	/** Duration and end are two views of the same value, so they share editability. */
	private syncDurationEditability(absence: AbsenceRowControl, empGroup: AbsenceEmployeeControl): void {
		const disabled = this.isDisabledForUser(absence, empGroup);
		for (const control of [absence.controls.hours, absence.controls.end]) {
			if (disabled) {
				control.disable({ emitEvent: false });
			} else {
				control.enable({ emitEvent: false });
			}
		}
		this.changeDetectorRef.markForCheck();
	}

	readonly absencesForUser = useAbsencesForUser();

	isAbsenceEnabledForSelection(subcategory: AbsenceSubcategory): boolean {
		const ids = this.employeeFilter.value;
		const userIds = ids.length ? ids : [this.userSettingsQuery.getValue().id];
		return userIds.every((id) => this.absencesForUser(subcategory, { value: { employee: { id } } }));
	}

	async submit() {
		const { employees } = this.absenceGroup.getRawValue();
		if (!employees || !employees.length) return;
		this.isLoading.update(true);
		//if any conflicts open absence conflict dilaog
		if (this._dayConflicts$.value.size > 0) {
			const employeesArray = this.absenceGroup.controls.employees;
			const hideSkip =
				employeesArray.controls.length === 1 && employeesArray.controls[0].controls.absences.length === 1;
			const employeeIds = new Set(this.employeeFilter.value);
			const absenceIntervalsByUser = this.buildAbsenceIntervalsByUser(employeeIds);
			const dialogRef = this.dialog.open(AbsenceConflictDialogComponent, {
				data: {
					conflicts: cloneConflictsMap(this._dayConflicts$.value),
					absenceIntervalsByUser: cloneAbsenceIntervalsByUser(absenceIntervalsByUser),
					range: { from: this.range.value.from, to: this.range.value.to },
					hideSkip: hideSkip,
					parent: this,
				},
			});
			dialogRef.afterClosed().subscribe((result: AbsenceConflictCloseResult | undefined) => {
				switch (result) {
					// "Overwrite"
					case "overwrite":
						this.overwriteConflicts();
						break;
					// "Skip"
					case "skip":
						this.skipConflicts();
						break;
					// "Cancel"
					case "cancel":
						void this.revalidateConflicts().finally(() => this.isLoading.update(false));
						break;
					default:
						this.isLoading.update(false);
						break;
				}
			});
		} else {
			this.saveAbsences();
		}
	}

	async overwriteConflicts() {
		const allWorkinghours = this.allWorkinghours$.value;
		const idsToDelete = new Set(allWorkinghours.map((wh) => wh.id).filter((id): id is string => !!id));

		try {
			if (idsToDelete.size > 0) {
				await this.comegoService.delete(...[...idsToDelete].map((id) => ({ id })));
			}
		} catch (error) {
			pushError(error);
			this.isLoading.update(false);
			return;
		}
		// remove employees with 0  eneteies
		this.absenceGroup.controls.employees.controls = this.absenceGroup.controls.employees.controls.filter(
			(user) => user.controls.absences.length > 0
		);
		if (this.absenceGroup.controls.employees.controls.length === 0) {
			this.ref.close();
			return;
		}
		this.saveAbsences();
	}
	skipConflicts() {
		// Remove new absences from the fromGroup, then save the absences without conflict
		const conflicts = this._dayConflicts$.value;
		const employeesArray = this.absenceGroup.controls.employees;
		for (const [userId, days] of conflicts.entries()) {
			const userIndex = employeesArray.controls.findIndex((user) => user.controls.employee.value.id === userId);
			if (userIndex === -1) continue;
			const employee = employeesArray.controls[userIndex];
			const absencesArray = employee.controls.absences;
			for (const day of days) {
				const dayIndex = absencesArray.controls.findIndex(
					(absence) => this.dayKey(absence.controls.date.value) === day
				);
				if (dayIndex === -1) continue;
				absencesArray.removeAt(dayIndex);
			}
		}
		// remove employees with 0  eneteies
		this.absenceGroup.controls.employees.controls = this.absenceGroup.controls.employees.controls.filter(
			(user) => user.controls.absences.length > 0
		);
		if (this.absenceGroup.controls.employees.controls.length === 0) {
			this.ref.close();
			return;
		}
		this.saveAbsences();
	}

	async saveAbsences() {
		const { employees } = this.absenceGroup.getRawValue();
		// remove employees with 0  eneteies
		this.absenceGroup.controls.employees.controls = this.absenceGroup.controls.employees.controls.filter(
			(user) => user.controls.absences.length > 0
		);
		if (this.absenceGroup.controls.employees.controls.length === 0) {
			this.ref.close();
			return;
		}
		if (!employees || !employees.length) return;
		const createAbsences: [string, ReturnType<ComeAndGoAbsenceDialogComponent["mapAbsenceRow"]>[]][] =
			employees.reduce(
				(acc, r) => {
					acc.push([r.employee.id, r.absences.map((absence) => this.mapAbsenceRow(r.employee.id, absence))]);
					return acc;
				},
				[] as [string, ReturnType<ComeAndGoAbsenceDialogComponent["mapAbsenceRow"]>[]][]
			);

		const results = await Promise.allSettled(
			createAbsences.map(([, absences]) =>
				this.comegoService
					.add(...absences)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
			)
		);

		const failed = results.some((r) => r.status === "rejected");
		if (failed) {
			results.forEach((result) => {
				if (result.status === "rejected") {
					pushError(result.reason as HttpErrorResponse);
				}
			});
		} else {
			this.app.notifySuccess("success.created-plural");
			this.ref.close();
		}
		this.isLoading.update(false);
	}

	private getAbsenceDurationSnapshot(): string {
		const employees = this.absenceGroup.getRawValue().employees ?? [];
		return stringify(
			employees.map((e) =>
				e.absences.map((a) => ({
					date: a.date instanceof Date ? a.date.toISOString() : a.date,
					hours: a.hours,
				}))
			)
		);
	}

	private async revalidateConflicts(): Promise<void> {
		const range = this.range.value;
		const employees = this.employeeFilter.value;
		if (!range?.from || !range?.to || !employees.length) {
			this.allWorkinghours$.update([]);
			this._dayConflicts$.next(new Map());
			this.changeDetectorRef.markForCheck();
			return;
		}

		try {
			const workingTimes = await this.comegoService.getBetween(range.from, range.to);
			const employeeIds = new Set(employees);
			const filteredByEmployee = workingTimes.filter((wh) => employeeIds.has(wh.user.id));
			const absenceIntervalsByUser = this.buildAbsenceIntervalsByUser(employeeIds);
			const { conflicts, relevantWorkingHours } = computeAbsenceConflicts(
				filteredByEmployee,
				absenceIntervalsByUser
			);
			this.allWorkinghours$.update(relevantWorkingHours);
			this._dayConflicts$.next(conflicts);
		} catch (error) {
			pushError(error);
		}
		this.changeDetectorRef.markForCheck();
	}

	private buildAbsenceIntervalsByUser(
		employeeIds: Set<string>
	): Map<string, { dayKey: string; start: Date; end: Date }[]> {
		const byUser = new Map<string, { dayKey: string; start: Date; end: Date }[]>();
		const formEmployees = this.absenceGroup.getRawValue().employees ?? [];

		for (const emp of formEmployees) {
			if (!employeeIds.has(emp.employee.id)) continue;
			for (const absence of emp.absences) {
				const mapped = this.mapAbsenceRow(emp.employee.id, absence);
				const absenceStart = new Date(mapped.start);
				const absenceEnd = new Date(mapped.end);
				if (!isValid(absenceStart) || !isValid(absenceEnd) || absenceStart >= absenceEnd) continue;

				const intervals = byUser.get(emp.employee.id) ?? [];
				intervals.push({
					dayKey: this.dayKey(absence.date),
					start: absenceStart,
					end: absenceEnd,
				});
				byUser.set(emp.employee.id, intervals);
			}
		}
		return byUser;
	}

	private mapAbsenceRow(employeeId: string, absence: AbsenceRowValue) {
		const startDate = coerceTimeFormat(absence.start || DEFAULT_ABSENCE_START, absence.date);
		const durationSeconds = parseDurationSeconds(absence.hours);
		const endDate = addSeconds(startDate, durationSeconds);
		const typeId = typeof absence.type === "object" && absence.type?.id ? absence.type.id : null;
		return {
			start: startDate.toISOString(),
			end: endDate.toISOString(),
			type: "absence" as const,
			typeId,
			user: { id: employeeId },
			name: null as string,
			timeZone: "Europe/Berlin",
		};
	}

	hasConflict(empGroup: AbsenceEmployeeControl, absence: AbsenceRowControl): boolean {
		const userId = empGroup.controls.employee.value.id;
		const date = absence.controls.date.value;
		if (!userId || !date || !isValid(date)) return false;
		return !!this._dayConflicts$.value.get(userId)?.has(this.dayKey(date));
	}

	userHasAnyConflict(empGroup: AbsenceEmployeeControl): boolean {
		const userId = empGroup.controls.employee.value.id;
		if (!userId) return false;
		const set = this._dayConflicts$.value.get(userId);
		if (!set?.size) return false;
		return empGroup.controls.absences.controls.some((a) => {
			const d = a.controls.date.value;
			return d && isValid(d) && set.has(this.dayKey(d));
		});
	}

	resolveAbsenceConflict(employee: AbsenceEmployeeControl) {
		// remove absence entes of employee with conflicts
		const conflicts = this._dayConflicts$.value.get(employee.controls.employee.value.id);
		if (!conflicts) return;
		const absencesArray = employee.controls.absences;
		// Collect indices to remove, then remove from the end to avoid index shifting
		const indicesToRemove: number[] = [];
		absencesArray.controls.forEach((absence, idx) => {
			if (conflicts.has(this.dayKey(absence.controls.date.value))) {
				indicesToRemove.push(idx);
			}
		});
		for (let i = indicesToRemove.length - 1; i >= 0; i--) {
			absencesArray.removeAt(indicesToRemove[i]);
		}
		this.recalculateEmployeeDurations(employee);
		this.changeDetectorRef.markForCheck();
	}
	returnToCreateDialog() {
		this.dialog.open(ComeAndGoCreateDialogComponent, {
			data: {} as WorkingHoursCreateData,
		});
		this.ref.close();
	}
}
